import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { LightbulbOutlined } from '@mui/icons-material';
import { useGlobalization } from '@Translations/useGlobalization';

const HELP_ID = 'risk-calculation-help';

// Same typography and disclosure pattern as Category/SetupIntro (LoF/CoF).
// Self-contained open state so it adds nothing to RiskSetup's complexity.
export const RiskSetupIntro = () => {
	const { t } = useGlobalization();
	const [helpOpen, setHelpOpen] = useState(false);
	const bullets = [
		t(
			'Overall risk combines two things: how likely a pipe is to fail (LoF) and how serious it would be if it did (CoF).',
		),
		t(
			'Combine method — Multiply means LoF × CoF (the classic “likelihood × consequence”); Add means LoF + CoF.',
		),
		t(
			'Weighting sets how much each side counts. Max Score is the most a side can contribute — e.g. LoF at 50% of a 10-point scale can add up to 5.0.',
		),
		t(
			'Risk Grade turns the final score into a label (Optimal → Critical) using the thresholds below. Normalization optionally rescales scores to a fixed range (e.g. 0–100).',
		),
	];
	return (
		<Box sx={{ mb: 3 }}>
			<Typography
				sx={{
					fontSize: 20,
					fontWeight: 700,
					color: '#0d1520',
					mb: 0.5,
				}}>
				{t('Risk Setup')}
			</Typography>
			<Typography sx={{ fontSize: 13, color: '#5b6472' }}>
				{t(
					// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
					"Risk Setup combines each pipe's LoF and CoF into one risk score, then assigns a risk grade.",
				)}
			</Typography>
			<Box
				component="button"
				type="button"
				data-cy="risk-calculation-help-toggle"
				onClick={() => setHelpOpen(o => !o)}
				aria-expanded={helpOpen}
				aria-controls={HELP_ID}
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 1,
					mt: 1.5,
					border: 'none',
					background: 'transparent',
					cursor: 'pointer',
					font: 'inherit',
					fontSize: 13,
					fontWeight: 700,
					color: '#1f2733',
					p: 0,
				}}>
				<Box
					component="span"
					sx={{
						fontSize: 11,
						color: '#97a0ac',
						transition: 'transform .2s ease',
						transform: helpOpen ? 'rotate(90deg)' : 'none',
					}}>
					▸
				</Box>
				<LightbulbOutlined sx={{ fontSize: 16, color: '#97a0ac' }} />
				{t('How risk is calculated')}
			</Box>
			<Collapse in={helpOpen}>
				<Box
					id={HELP_ID}
					sx={{
						mt: 1.25,
						fontSize: 13,
						color: '#3a4451',
						maxWidth: 900,
					}}>
					<Box component="ul" sx={{ pl: 2.5, m: 0 }}>
						{bullets.map((bullet, i) => (
							<Typography
								key={bullet}
								component="li"
								sx={{
									fontSize: 13,
									mb: i < bullets.length - 1 ? 0.5 : 0,
								}}>
								{bullet}
							</Typography>
						))}
					</Box>
				</Box>
			</Collapse>
		</Box>
	);
};
